import {
  CalendarCheckIcon,
  CaretLeftIcon,
  CarProfileIcon,
  ChatsIcon,
  MapPinIcon,
  QuestionMarkIcon,
  StarIcon,
} from '@phosphor-icons/react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { useRef, useState } from 'react'
import { MyBLINK } from '@/src/@types/blinkadmin'
import Button from '@/src/components/core/Button'
import ConditionalWrapper from '@/src/components/core/ConditionalWrapper'
import { DialogBody, DialogHeader } from '@/src/components/core/Dialog'
import { formatGerman } from '@/src/components/core/lib/date'
import ShowMore from '@/src/components/core/ShowMore'
import {
  FactsIconMap,
  getColorFromString,
} from '@/src/components/core/teamProfile'
import Picture from '@/src/components/elements/Picture'

const Profile = ({
  instructor,
  confirm,
  deselect,
}: {
  instructor: MyBLINK.BookableUser
  confirm: () => void
  deselect: () => void
}) => {
  const tabRef = useRef<HTMLDivElement>(null)
  const [tab, setTab] = useState<'info' | 'feedback'>('info')

  instructor.feedback.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  )

  return (
    <>
      <DialogHeader title={`Das ist ${instructor.name}!`} />
      <DialogBody className="md:flex md:flex-col md:pr-0 md:pb-0 lg:pr-0">
        <button
          onClick={deselect}
          className="inline-flex items-center space-x-2 place-self-start text-sm font-bold text-purple-600"
        >
          <CaretLeftIcon size={20} weight="bold" />
          <span>Zurück zu den Fahrlehrer:innen</span>
        </button>
        <div className="md:flex md:space-x-8 md:overflow-y-auto">
          <div className="shrink-0 md:w-70">
            <div className="relative mt-6 aspect-[1.47]">
              <Picture
                permalink={instructor.photo}
                alt={instructor.name}
                fill
                sizes="400px"
                className="rounded-md object-cover object-top"
              />
            </div>
            <p className="mt-6 text-center text-2xl font-black">
              {instructor.name}
            </p>
            <p className="mt-0.5 text-center text-sm text-gray-600">
              {instructor.shortDescription}
            </p>
            {instructor.shortSentence && (
              <p className="mt-4 text-center text-gray-800">
                {instructor.shortSentence}
              </p>
            )}
            <Button onClick={confirm} className="mt-4 w-full md:mb-8">
              Für Probelektion auswählen
            </Button>
          </div>
          <div className="flex max-w-full grow flex-col">
            {instructor.feedback.length !== 0 && (
              <div
                ref={tabRef}
                className="-mx-5 mt-8 grid scroll-mt-5 grid-cols-2 space-x-5 border-b border-blue-100 px-5 md:-mx-0 md:mt-0 md:block md:px-0"
              >
                <button
                  className={clsx('border-b-2 pt-2.5 pb-1.5', {
                    'border-purple-600 font-bold': tab === 'info',
                    'border-transparent': tab !== 'info',
                  })}
                  onClick={() => setTab('info')}
                >
                  Informationen
                </button>
                <button
                  className={clsx('border-b-2 pt-2.5 pb-1.5', {
                    'border-purple-600 font-bold': tab === 'feedback',
                    'border-transparent': tab !== 'feedback',
                  })}
                  onClick={() => setTab('feedback')}
                >
                  Feedbacks
                </button>
              </div>
            )}
            {tab === 'info' && (
              <div className="overflow-y-auto pt-6 md:py-6 md:pr-6">
                <p className="font-bold">Steckbrief</p>
                <div className="mt-3 space-y-2">
                  {instructor.since && (
                    <div className="flex items-center space-x-3">
                      <div className="rounded-full bg-blue-50 p-2">
                        <CalendarCheckIcon
                          size={24}
                          weight="fill"
                          className="text-gray-600"
                        />
                      </div>
                      <p>
                        {instructor.shortDescription.includes('Fahrlehrerin')
                          ? 'Fahrlehrerin'
                          : 'Fahrlehrer'}{' '}
                        seit {instructor.since}
                      </p>
                    </div>
                  )}
                  {instructor.languages.length > 0 && (
                    <div className="flex items-center space-x-3">
                      <div className="rounded-full bg-blue-50 p-2">
                        <ChatsIcon
                          size={24}
                          weight="fill"
                          className="text-gray-600"
                        />
                      </div>
                      <p>{instructor.languages.join(', ')}</p>
                    </div>
                  )}
                  <div className="flex items-center space-x-3">
                    <div className="rounded-full bg-blue-50 p-2">
                      <MapPinIcon
                        size={24}
                        weight="fill"
                        className="text-gray-600"
                      />
                    </div>
                    <p>
                      {instructor.locations
                        .map((location) => location.name)
                        .join(', ')}
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="rounded-full bg-blue-50 p-2">
                      <CarProfileIcon
                        size={24}
                        weight="fill"
                        className="text-gray-600"
                      />
                    </div>
                    <p>
                      {instructor.cars.map((car) => car.name).join(', ')},
                      Automat{' '}
                      {instructor.cars.some(
                        (car) => car.transmission_type === 'manual'
                      )
                        ? '& Geschalten'
                        : ''}
                    </p>
                  </div>
                </div>
                {instructor.facts.length > 0 && (
                  <>
                    <p className="mt-8 font-bold">
                      Facts über {instructor.name}
                    </p>
                    <div className="mt-3 space-y-2">
                      {instructor.facts.map((fact) => (
                        <div
                          key={fact.text}
                          className="flex items-center space-x-3"
                        >
                          <div className="rounded-full bg-blue-100 p-2">
                            {FactsIconMap[fact.icon] ? (
                              <>{FactsIconMap[fact.icon]}</>
                            ) : (
                              <QuestionMarkIcon
                                size={24}
                                weight="bold"
                                className="text-blue-600"
                              />
                            )}
                          </div>
                          <p>{fact.text}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                <p className="mt-8 font-bold">Feedbacks von Schüler:innen</p>
                <div className="mt-1">
                  {instructor.feedback.length > 0 && (
                    <>
                      {instructor.feedback.slice(0, 3).map((feedback) => (
                        <div key={feedback.author} className="mt-3">
                          <div className="rounded-lg bg-blue-50 p-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-start space-x-4">
                                {feedback.author_image_url &&
                                  feedback.author_image_url.includes(
                                    'lh3.googleusercontent.com/a-/'
                                  ) && (
                                    <Image
                                      sizes="48px"
                                      alt={feedback.author}
                                      src={feedback.author_image_url}
                                      height={48}
                                      width={48}
                                      className="shrink-0 rounded-full object-cover"
                                      unoptimized
                                    />
                                  )}
                                {!(
                                  feedback.author_image_url &&
                                  feedback.author_image_url.includes(
                                    'lh3.googleusercontent.com/a-/'
                                  )
                                ) && (
                                  <p
                                    className={clsx(
                                      'flex size-12 shrink-0 items-center justify-center rounded-full text-center text-xl',
                                      getColorFromString(feedback.author)
                                    )}
                                  >
                                    {feedback.author.trim()[0].toUpperCase()}
                                  </p>
                                )}
                                <div>
                                  <p className="font-bold">{feedback.author}</p>
                                  <p className="text-gray-600">
                                    {formatGerman(
                                      new Date(feedback.date),
                                      'dd. MMMM yyyy'
                                    )}
                                  </p>
                                </div>
                              </div>
                              <ConditionalWrapper
                                when={feedback.url}
                                wrapper={(children, url) => (
                                  <Link href={url} target="_blank">
                                    {children}
                                  </Link>
                                )}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  className="size-6"
                                >
                                  <path
                                    fill="#4285F4"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                  />
                                  <path
                                    fill="#34A853"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                  />
                                  <path
                                    fill="#FBBC05"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                  />
                                  <path
                                    fill="#EA4335"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                  />
                                  <path fill="none" d="M1 1h22v22H1z" />
                                </svg>
                              </ConditionalWrapper>
                            </div>
                            <div className="mt-6 flex space-x-1">
                              {new Array(Math.ceil(feedback.rating))
                                .fill(null)
                                .map((_, index) => (
                                  <StarIcon
                                    key={index}
                                    size={20}
                                    weight="fill"
                                    className="shrink-0 text-yellow-600"
                                  />
                                ))}
                              {new Array(Math.floor(5 - feedback.rating))
                                .fill(null)
                                .map((_, index) => (
                                  <StarIcon
                                    key={index}
                                    size={20}
                                    className="shrink-0 text-yellow-600"
                                  />
                                ))}
                            </div>
                            <ShowMore>
                              {({
                                clampRef,
                                isClamped,
                                isExpanded,
                                setIsExpanded,
                              }) => (
                                <>
                                  <p
                                    ref={clampRef}
                                    className={clsx('mt-4 text-gray-800', {
                                      'line-clamp-4': !isExpanded,
                                    })}
                                  >
                                    {feedback.text}
                                  </p>
                                  {isClamped && !isExpanded && (
                                    <button
                                      onClick={() => setIsExpanded(true)}
                                      className="mt-4 text-sm font-bold text-purple-600 hover:text-purple-800"
                                    >
                                      Mehr anzeigen
                                    </button>
                                  )}
                                </>
                              )}
                            </ShowMore>
                          </div>
                        </div>
                      ))}
                      {instructor.feedback.length > 3 && (
                        <div className="mt-4 flex justify-center">
                          <button
                            onClick={() => {
                              setTab('feedback')
                              tabRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                              })
                            }}
                            className="text-sm font-bold text-purple-600 hover:text-purple-800"
                          >
                            Alle Feedbacks anzeigen
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  {instructor.feedback.length === 0 && (
                    <p className="mt-4 text-gray-800">
                      {instructor.name} hat noch keine Feedbacks erhalten.
                    </p>
                  )}
                </div>
              </div>
            )}
            {tab === 'feedback' && (
              <div className="overflow-y-auto pt-6 md:py-6 md:pr-6">
                <p className="font-bold">Feedbacks von Schüler:innen</p>
                <div className="mt-1">
                  {instructor.feedback.map((feedback) => (
                    <div key={feedback.author} className="mt-3">
                      <div className="rounded-lg bg-blue-50 p-6">
                        <div className="flex justify-between">
                          <div className="flex items-start space-x-4">
                            {feedback.author_image_url &&
                              feedback.author_image_url.includes(
                                'lh3.googleusercontent.com/a-/'
                              ) && (
                                <Image
                                  sizes="48px"
                                  alt={feedback.author}
                                  src={feedback.author_image_url}
                                  height={48}
                                  width={48}
                                  className="shrink-0 rounded-full object-cover"
                                  unoptimized
                                />
                              )}
                            {!(
                              feedback.author_image_url &&
                              feedback.author_image_url.includes(
                                'lh3.googleusercontent.com/a-/'
                              )
                            ) && (
                              <p
                                className={clsx(
                                  'flex size-12 shrink-0 items-center justify-center rounded-full text-center text-xl',
                                  getColorFromString(feedback.author)
                                )}
                              >
                                {feedback.author.trim()[0].toUpperCase()}
                              </p>
                            )}
                            <div>
                              <p className="font-bold">{feedback.author}</p>
                              <p className="text-gray-600">
                                {formatGerman(
                                  new Date(feedback.date),
                                  'dd. MMMM yyyy'
                                )}
                              </p>
                            </div>
                          </div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className="size-6"
                          >
                            <path
                              fill="#4285F4"
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                              fill="#34A853"
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                              fill="#FBBC05"
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            />
                            <path
                              fill="#EA4335"
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            />
                            <path fill="none" d="M1 1h22v22H1z" />
                          </svg>
                        </div>
                        <div className="mt-6 flex space-x-1">
                          {new Array(Math.ceil(feedback.rating))
                            .fill(null)
                            .map((_, index) => (
                              <StarIcon
                                key={index}
                                size={20}
                                weight="fill"
                                className="shrink-0 text-yellow-600"
                              />
                            ))}
                          {new Array(Math.floor(5 - feedback.rating))
                            .fill(null)
                            .map((_, index) => (
                              <StarIcon
                                key={index}
                                size={20}
                                className="shrink-0 text-yellow-600"
                              />
                            ))}
                        </div>
                        <ShowMore>
                          {({
                            clampRef,
                            isClamped,
                            isExpanded,
                            setIsExpanded,
                          }) => (
                            <>
                              <p
                                ref={clampRef}
                                className={clsx('mt-4 text-gray-800', {
                                  'line-clamp-4': !isExpanded,
                                })}
                              >
                                {feedback.text}
                              </p>
                              {isClamped && !isExpanded && (
                                <button
                                  onClick={() => setIsExpanded(true)}
                                  className="mt-4 text-sm font-bold text-purple-600 hover:text-purple-800"
                                >
                                  Mehr anzeigen
                                </button>
                              )}
                            </>
                          )}
                        </ShowMore>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {instructor.feedback.length !== 0 && (
              <>
                <Button onClick={confirm} className="mt-12 w-full md:hidden">
                  {instructor.name} für Probelektion auswählen
                </Button>
                <Button
                  version="gray-ghost"
                  onClick={deselect}
                  className="mt-4 w-full md:hidden"
                >
                  Zurück zu den Fahrlehrer:innen
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogBody>
    </>
  )
}

export default Profile
